/**
 * Session presets (SPEC §2, §8): the `demo` session the app boots into so
 * the empty state is never silent, and the `blank` session behind
 * "New session". Both share the same groove — A minor · 82 bpm · swing 30 ·
 * lofi — and default track volumes; `demo` adds the 14-note lead riff,
 * authored `by: 'human'` so the first agent write is visibly the first teal
 * thing in the grid. Neither preset contains drums, bass or chords.
 */
import type { MelodicTrackId, Note, Session, TrackId } from '../types';
import { DEFAULT_TRACK_VOLUME_DB, STEPS_PER_BAR } from '../types';

/** Preset identifiers accepted by `createSession`. */
export type PresetId = 'demo' | 'blank';

/** Velocity of a demo riff note on a bar downbeat. */
export const DEMO_DOWNBEAT_VELOCITY = 0.95;
/** Velocity of every other demo riff note. */
export const DEMO_VELOCITY = 0.8;

const A4 = 69;
const B4 = 71;
const C5 = 72;
const D5 = 74;
const E5 = 76;

/** `[bar 1..4, step 1..16, pitch, length]` — SPEC §8 verbatim. */
const RIFF_SOURCE: ReadonlyArray<readonly [bar: number, step: number, pitch: number, length: number]> = [
  // b1 A4@1 L2, C5@5 L2, E5@9 L2, D5@13 L2
  [1, 1, A4, 2],
  [1, 5, C5, 2],
  [1, 9, E5, 2],
  [1, 13, D5, 2],
  // b2 C5@1 L2, B4@5 L2, A4@9 L6
  [2, 1, C5, 2],
  [2, 5, B4, 2],
  [2, 9, A4, 6],
  // b3 A4@1 L2, C5@3 L2, E5@5 L4, D5@9 L2, C5@13 L2
  [3, 1, A4, 2],
  [3, 3, C5, 2],
  [3, 5, E5, 4],
  [3, 9, D5, 2],
  [3, 13, C5, 2],
  // b4 B4@1 L4, A4@9 L6
  [4, 1, B4, 4],
  [4, 9, A4, 6],
];

/**
 * The demo lead riff (SPEC §8): 14 human-authored notes in A minor, velocity
 * .8 with .95 on each bar's downbeat. Frozen; `createSession('demo')` copies it.
 */
export const DEMO_RIFF: readonly Readonly<Note>[] = Object.freeze(
  RIFF_SOURCE.map(([bar, step, pitch, length]) => {
    const stepInBar = step - 1;
    return Object.freeze({
      step: (bar - 1) * STEPS_PER_BAR + stepInBar,
      pitch,
      length,
      velocity: stepInBar === 0 ? DEMO_DOWNBEAT_VELOCITY : DEMO_VELOCITY,
      by: 'human' as const,
    });
  }),
);

/** A fresh, mutable copy of `DEMO_RIFF` (for callers that need a plain `Note[]`). */
export function demoRiff(): Note[] {
  return DEMO_RIFF.map((n) => ({ ...n }));
}

function melodicTrack(id: MelodicTrackId, notes: Note[]): Session['tracks'][MelodicTrackId] {
  return { id, notes, muted: false, solo: false, volume: DEFAULT_TRACK_VOLUME_DB[id] };
}

function volumeOf(id: TrackId): number {
  return DEFAULT_TRACK_VOLUME_DB[id];
}

/**
 * Build a fresh session for a preset. `demo` carries the lead riff; `blank`
 * has every track empty. Both are A minor, 82 bpm, swing 30, lofi, metronome
 * off, default volumes, no progression/bass metadata, lead track and bar 1
 * selected. Every call returns a new, independently mutable object.
 */
export function createSession(preset: PresetId): Session {
  const lead: Note[] = preset === 'demo' ? demoRiff() : [];
  return {
    version: 1,
    key: { tonic: 'A', mode: 'minor' },
    tempo: 82,
    swing: 30,
    style: 'lofi',
    metronome: false,
    tracks: {
      drums: { id: 'drums', hits: [], muted: false, solo: false, volume: volumeOf('drums') },
      bass: melodicTrack('bass', []),
      chords: melodicTrack('chords', []),
      lead: melodicTrack('lead', lead),
    },
    progression: null,
    progressionRhythm: null,
    bassPattern: null,
    ui: { selectedTrack: 'lead', selectedBar: 0 },
  };
}
